import {navigate} from 'gatsby';
import * as React from 'react';
import * as queryString from 'query-string';
import {Trans, withTranslation, WithTranslation} from 'react-i18next';

import i18n from '@src/i18n';
import {getZipCode, setZipCode} from '@src/utils/persistence';
import * as zipCodes from '@src/utils/zipCodes';

import * as styles from './countyForm.module.scss';

import type {
  Pbps,
  SiteSiteMetadataSsaCountiesForPbps,
  SiteSiteMetadataSsaCountiesForPbpsZip_Codes,
  SiteSiteMetadataSupportedCounties,
} from '@graphql-types';

type Counties = Pick<
  SiteSiteMetadataSsaCountiesForPbps,
  'code' | 'display_name' | 'state_abbreviation'
> & {
  zip_codes: Array<
    Pick<SiteSiteMetadataSsaCountiesForPbpsZip_Codes, 'zip_code'>
  >;
};

type PlanBenefitPackages = Pick<
  Pbps,
  'csnp_conditions' | 'year' | 'name' | 'record_locator'
> & {
  plan: Pick<Pbps['plan'], 'ma_variant' | 'plan_type'>;
  service_areas: Array<Pick<Pbps['service_areas'][number], 'county_code'>>;
};

type SupportedCounties = Pick<
  SiteSiteMetadataSupportedCounties,
  'county_code' | 'pages'
>;

export interface CountyFormProps extends WithTranslation<'PlanFinder'> {
  activePlanYear: number;
  planBenefitPackages: PlanBenefitPackages[];
  ssaCounties: Counties[];
  supportedCounties: SupportedCounties[];
  isHome?: boolean;
  className?: string;
}

export interface CountyFormState {
  activePlanBenefitPackages: PlanBenefitPackages[];
  disableButton: boolean;
  latestPlanYear: number;
  planBenefitPackagesInSelectableCounties: PlanBenefitPackages[];
  selectableCounties: Counties[];
  selectedCounty: Counties | null;
  shouldNavigate: boolean;
  showCountyDropdown: boolean;
  showError: boolean;
  zipcode: string;
}

export class UntranslatedCountyForm extends React.Component<
  CountyFormProps,
  CountyFormState
> {
  zipInput: HTMLInputElement;
  form: HTMLFormElement;

  constructor(props: CountyFormProps) {
    super(props);

    const latestPlanYear = props.activePlanYear;

    this.state = {
      activePlanBenefitPackages: props.planBenefitPackages.filter(
        pbp => parseInt(pbp.year, 10) <= latestPlanYear,
      ),
      disableButton: true,
      latestPlanYear,
      planBenefitPackagesInSelectableCounties: [],
      selectableCounties: [],
      selectedCounty: null,
      shouldNavigate: false,
      showCountyDropdown: false,
      showError: false,
      zipcode: '',
    };
  }

  componentDidMount() {
    const urlParams = queryString.parse(window.location.search);
    const urlZipCode = urlParams.zipcode;
    const storedZipCode = getZipCode();
    const zipCode = urlZipCode || storedZipCode;

    this.zipInput.value = zipCode;

    if (zipCode) {
      this.setSelectedLocation(zipCode);
    }

    // If zipcode is provided as a URL query param it likely is due to a redirect
    // from a landing page. Automatically navigate to the plans page for the county.
    if (urlZipCode) {
      this.setState({shouldNavigate: true});
    }
  }

  componentDidUpdate() {
    const {selectedCounty, shouldNavigate, showError} = this.state;

    if (!selectedCounty || !shouldNavigate || showError) {
      return;
    }

    const supportedCounty = this.props.supportedCounties.find(
      county => county.county_code === selectedCounty.code,
    );

    if (supportedCounty?.pages.length) {
      // If current page is V2, prefer V2 paths; otherwise prefer non -v2
      const isCurrentV2 =
        typeof window !== 'undefined' &&
        window.location &&
        window.location.pathname.includes('-v2');

      const preferred = supportedCounty.pages.find(p =>
        isCurrentV2
          ? p.lang === i18n.language && p.path.includes('-v2')
          : p.lang === i18n.language && !p.path.includes('-v2'),
      );
      const fallback = supportedCounty.pages.find(
        p => p.lang === i18n.language,
      );
      const page = preferred || fallback;

      const params = queryString.parse(window.location.search);
      const nextParams: Record<string, any> = {};

      // propagate from a landing page so the thank you banner is displayed
      if (params.thank_you) {
        nextParams.thank_you = true;
      }

      let query = queryString.stringify(nextParams);
      if (query.length > 0) {
        query = '?' + query;
      }

      navigate(page.path + query);
    } else {
      this.setState({showError: true});
    }
  }

  onChangeZip = (e: React.FormEvent<HTMLInputElement>) => {
    const zipcode = zipCodes.clean(e.currentTarget.value);

    if (!zipCodes.isValid(zipcode)) {
      this.setState({
        selectedCounty: null,
        showCountyDropdown: false,
      });
      return;
    }

    this.setSelectedLocation(zipcode);
  };

  setSelectedLocation = (value: string) => {
    let selectableCounties = this.props.ssaCounties
      .filter(county =>
        county.zip_codes.some(zipCode => zipCode.zip_code === value),
      )
      .sort((a, b) => a.display_name.localeCompare(b.display_name));

    const selectableCountyCodes = selectableCounties.map(c => c.code);
    const planBenefitPackagesInSelectableCounties = selectableCounties.length
      ? this.state.activePlanBenefitPackages.filter(pbp =>
          pbp.service_areas.some(serviceArea =>
            selectableCountyCodes.includes(serviceArea.county_code),
          ),
        )
      : [];

    if (planBenefitPackagesInSelectableCounties.length === 0) {
      // don't bother with a county picker if no PBPs are available
      selectableCounties = [];
    }

    const selectedCounty = selectableCounties.length
      ? selectableCounties[0]
      : null;

    this.setState({
      disableButton: false,
      planBenefitPackagesInSelectableCounties,
      selectableCounties,
      selectedCounty,
      showCountyDropdown: selectableCounties.length > 1,
      showError: false,
      zipcode: value,
    });
  };

  onChangeCounty = (e: React.FormEvent<HTMLSelectElement>) => {
    const selectedCounty = this.state.selectableCounties.find(
      c => c.code === e.currentTarget.value,
    );
    this.setState({
      selectedCounty,
    });
  };

  onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const {selectedCounty, disableButton, zipcode} = this.state;

    if (disableButton) {
      this.form.checkValidity();
    } else if (selectedCounty) {
      setZipCode(zipcode);
      this.setState({shouldNavigate: true, showError: false});
    } else {
      this.setState({showError: true});
    }
  };

  changeLocation = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();

    this.zipInput.value = '';

    this.setState({
      selectedCounty: null,
      showCountyDropdown: false,
      showError: false,
    });
  };

  render() {
    const {t, isHome, className} = this.props;
    const {selectableCounties, selectedCounty, showCountyDropdown, showError} =
      this.state;

    // the stylesheet will hide certain elements
    // they also need to be kept out of the DOM for React testing library
    // that library can't take styles into account when determining visiblity

    return (
      <div
        className={`${styles.wrapper} ${showError ? styles.error : null}  ${
          isHome ? styles.home : null
        } ${className || ''}`}>
        <form onSubmit={this.onSubmit} ref={el => (this.form = el)}>
          <div className={styles.row}>
            <label
              htmlFor="zipcode"
              className={`${isHome ? styles.orange : null}`}>
              {t('zipCodeLabel')}
            </label>
            <input
              id="zipcode"
              type="text"
              pattern="[0-9]*"
              title={t('zipCodeLabel')}
              onChange={this.onChangeZip}
              ref={input => (this.zipInput = input)}
              data-testid="zipcode"
            />
          </div>

          {showCountyDropdown && (
            <div
              className={`${styles.countyDropdown} ${
                showCountyDropdown ? styles.active : null
              }`}>
              <label htmlFor="counties">{t('countyLabel')}</label>
              <div className={styles.select}>
                <select
                  id="counties"
                  defaultValue={selectedCounty?.code ?? ''}
                  onChange={this.onChangeCounty}
                  onBlur={this.onChangeCounty}
                  data-testid="counties">
                  {selectableCounties.map((county, i) => (
                    <option
                      key={i}
                      value={county.code}
                      aria-selected={selectedCounty?.code === county.code}>
                      {t('countyName', {name: county.display_name})},{' '}
                      {county.state_abbreviation}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
          <div className={styles.row}>
            <button className={`${styles.button}`} data-testid="find-plan">
              {t('findPlanButton')}
            </button>
          </div>
        </form>

        <div className={styles.row}>
          {showError && (
            <div className={styles.errorMessage} data-testid="error-message">
              <Trans i18nKey="errorMessage" parent="p" t={t}>
                <strong>Sorry, we're not in your area yet!</strong>
              </Trans>
              <p>
                <span className={styles.changeLocationLink}>
                  {t('differentZipCode')}{' '}
                  <a href="" onClick={this.changeLocation}>
                    {t('differentZipCodeLink')}
                  </a>
                  .
                </span>
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default withTranslation('PlanFinder')(UntranslatedCountyForm);
